import { useState, useEffect } from 'react';
import api from '../services/api';
import { achievements } from '../data/achievements';
import useCurrentUser from '../hooks/useCurrentUser';

export default function AchievementsPage({ checkAndUnlock }) {
    const currentUser = useCurrentUser();
    const [allUsers, setAllUsers] = useState([]);
    const [allAchievements, setAllAchievements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedUser, setSelectedUser] = useState(null);
    const [colleguesVus, setColleguesVus] = useState(new Set());

    useEffect(() => {
        loadAll();
    }, []);

    const loadAll = async () => {
        try {
            const usersRes = await api.get('/users');
            setAllUsers(usersRes.data.member || []);
            const achievementsRes = await api.get('/user_achievements?pagination=false');
            setAllAchievements(achievementsRes.data.member || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const getUnlockedCodes = (userIri) => {
        return allAchievements
            .filter(a => a.user === userIri)
            .map(a => a.achievementCode);
    };

    const getScore = (userIri) => {
        const codes = getUnlockedCodes(userIri);
        return achievements
            .filter(a => codes.includes(a.code))
            .reduce((sum, a) => sum + (a.points || 0), 0);
    };

    const me = allUsers.find(u => u.email === currentUser?.email);
    const myUnlockedCodes = me ? getUnlockedCodes(me['@id']) : [];
    const myUnlockedCount = myUnlockedCodes.length;

    const classement = [...allUsers]
        .map(u => ({ user: u, score: getScore(u['@id']), count: getUnlockedCodes(u['@id']).length }))
        .sort((a, b) => b.score - a.score);

    if (loading) return <div style={{ padding: '24px', textAlign: 'center', color: '#888' }}>Chargement...</div>;

    return (
        <div style={{ padding: '24px', maxWidth: '900px' }}>
            <h2 style={{ margin: '0 0 8px', fontSize: '22px', fontWeight: '700', color: 'var(--text-main)' }}>
                <i className="ti ti-trophy" style={{ marginRight: '8px' }} />Hauts faits
            </h2>
            <p style={{ margin: '0 0 24px', fontSize: '14px', color: 'var(--text-muted)' }}>
                {myUnlockedCount} / {achievements.length} débloqué{myUnlockedCount > 1 ? 's' : ''}
                {me && <span style={{ marginLeft: '12px', fontWeight: '700', color: 'var(--accent-color)' }}>
                    {getScore(me['@id'])} pts
                </span>}
            </p>

            {/* Classement */}
            <div style={{
                backgroundColor: 'var(--card-bg)',
                borderRadius: '12px',
                padding: '16px 20px',
                marginBottom: '24px',
                boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
            }}>
                <h3 style={{ margin: '0 0 16px', fontSize: '15px', fontWeight: '700' }}><i className="ti ti-medal" style={{ marginRight: '6px' }} />Classement</h3>
                {classement.map((entry, index) => {
                    const isMe = entry.user['@id'] === me?.['@id'];
                    const isSelected = selectedUser?.['@id'] === entry.user['@id'];
                    return (
                        <div
                            key={entry.user['@id']}
                            onClick={() => {
                                setSelectedUser(isSelected ? null : entry.user);
                                if (!isMe && !isSelected) {
                                    const newSet = new Set(colleguesVus).add(entry.user['@id']);
                                    setColleguesVus(newSet);
                                    if (newSet.size >= 3) {
                                        checkAndUnlock('social');
                                    }
                                }
                            }}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                padding: '10px 12px',
                                borderRadius: '8px',
                                marginBottom: '6px',
                                cursor: 'pointer',
                                backgroundColor: isSelected ? '#ede9fe' : isMe ? '#f5f3ff' : 'var(--hover-bg)',
                                border: isSelected ? '1.5px solid var(--accent-color)' : '1.5px solid transparent',
                            }}
                        >
                            <div style={{
                                width: '28px',
                                fontWeight: '700',
                                fontSize: '14px',
                                color: index === 0 ? '#f59e0b' : index === 1 ? '#94a3b8' : index === 2 ? '#b45309' : 'var(--text-muted)',
                                textAlign: 'center',
                            }}>
                                {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `#${index + 1}`}
                            </div>
                            <div style={{ flex: 1, fontWeight: isMe ? '700' : '500', color: 'var(--text-main)', fontSize: '14px' }}>
                                {entry.user.nom || entry.user.email}
                                {isMe && <span style={{ fontSize: '11px', color: 'var(--accent-color)', marginLeft: '6px' }}>(moi)</span>}
                            </div>
                            <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                                {entry.count} haut{entry.count > 1 ? 's' : ''} fait{entry.count > 1 ? 's' : ''}
                            </div>
                            <div style={{
                                fontWeight: '700',
                                fontSize: '14px',
                                color: 'var(--accent-color)',
                                minWidth: '60px',
                                textAlign: 'right',
                            }}>
                                {entry.score} pts
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Hauts faits du collègue sélectionné */}
            {selectedUser && selectedUser['@id'] !== me?.['@id'] && (
                <div style={{
                    backgroundColor: 'var(--card-bg)',
                    borderRadius: '12px',
                    padding: '16px 20px',
                    marginBottom: '24px',
                    boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
                }}>
                    <h3 style={{ margin: '0 0 16px', fontSize: '15px', fontWeight: '700' }}>
                        Hauts faits de {selectedUser.nom}
                    </h3>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {achievements.map(a => {
                            const unlocked = getUnlockedCodes(selectedUser['@id']).includes(a.code);
                            return (
                                <div key={a.code} style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    padding: '6px 12px',
                                    borderRadius: '20px',
                                    backgroundColor: unlocked ? '#ede9fe' : 'var(--hover-bg)',
                                    border: `1.5px solid ${unlocked ? 'var(--accent-color)' : 'transparent'}`,
                                    opacity: unlocked ? 1 : 0.4,
                                    filter: unlocked ? 'none' : 'grayscale(1)',
                                }}>
                                    <span style={{ fontSize: '16px' }}>{a.icon}</span>
                                    <span style={{ fontSize: '12px', fontWeight: '600', color: unlocked ? 'var(--accent-color)' : 'var(--text-muted)' }}>
                                        {unlocked ? a.title : '???'}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Mes hauts faits */}
            <h3 style={{ margin: '0 0 16px', fontSize: '15px', fontWeight: '700', color: 'var(--text-main)' }}>
                Mes hauts faits
            </h3>
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                gap: '14px',
            }}>
                {achievements.map(a => {
                    const unlocked = myUnlockedCodes.includes(a.code);
                    return (
                        <div key={a.code} style={{
                            backgroundColor: 'var(--card-bg)',
                            borderRadius: '12px',
                            padding: '16px',
                            boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '14px',
                            opacity: unlocked ? 1 : 0.5,
                            border: unlocked ? '2px solid #fbbf24' : '2px solid transparent',
                        }}>
                            <div style={{ fontSize: '32px', filter: unlocked ? 'none' : 'grayscale(1)', flexShrink: 0 }}>
                                {a.icon}
                            </div>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-main)' }}>
                                    {a.title}
                                </div>
                                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
                                    {unlocked ? a.description : '???'}
                                </div>
                                <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--accent-color)', marginTop: '4px' }}>
                                    {a.points} pts
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}